import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { CountrySelect } from './ui/CountrySelect';
import { CurrencySelect } from './ui/CurrencySelect';
import { RAILS } from '../lib/catalogs';
import { useCreateVendor, useUpdateVendor, type Vendor, type VendorInput } from '../hooks/useVendors';

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

/** Payment-rail picker built on the Radix Select primitive, populated from RAILS. */
function RailSelect({ value, onChange, placeholder }: { value: string; onChange: (next: string) => void; placeholder?: string }) {
  const { t } = useTranslation();
  return (
    <Select.Root value={value || undefined} onValueChange={onChange}>
      <Select.Trigger
        className={`${inputCn} mt-0.5 flex items-center justify-between gap-2 text-left`}
        aria-label={t('vendors.drawer.preferredRail')}
      >
        <Select.Value placeholder={placeholder} />
        <Select.Icon>
          <ChevronDown size={14} className="text-ink-400 shrink-0" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={6}
          className="z-50 min-w-[var(--radix-select-trigger-width)] rounded-xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 shadow-lg p-1 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <Select.Viewport>
            {RAILS.map((rail) => (
              <Select.Item
                key={rail.code}
                value={rail.code}
                className="relative flex items-center gap-2 pl-7 pr-3 py-1.5 rounded-md text-sm cursor-pointer outline-none text-ink-700 dark:text-ink-200 data-[highlighted]:bg-ink-100 dark:data-[highlighted]:bg-ink-800"
              >
                <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                  <Check size={14} className="text-accent-600" />
                </Select.ItemIndicator>
                <Select.ItemText>{t(rail.label)}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

type Draft = {
  legalName: string; displayName: string; countryCode: string; defaultCurrency: string;
  email: string; phone: string; address: string; taxId: string;
  jpCorpNumber: string; jpInvoiceRegistration: string; preferredRail: string;
};

function fromVendor(v?: Vendor | null): Draft {
  return {
    legalName: v?.legal_name ?? '', displayName: v?.display_name ?? '', countryCode: v?.country_code ?? '',
    defaultCurrency: v?.default_currency ?? '', email: v?.email ?? '', phone: v?.phone ?? '', address: v?.address ?? '',
    taxId: v?.tax_id ?? '', jpCorpNumber: v?.jp_corp_number ?? '', jpInvoiceRegistration: v?.jp_invoice_registration ?? '',
    preferredRail: v?.preferred_rail ?? '',
  };
}

/** Create a new vendor or edit an existing one. Pass `vendor` to edit. */
export function VendorFormDrawer({
  wsId, open, onOpenChange, vendor, onSaved,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  vendor?: Vendor | null;
  onSaved?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateVendor(wsId);
  const update = useUpdateVendor(wsId);
  const [d, setD] = useState<Draft>(fromVendor(vendor));
  const [error, setError] = useState<string | null>(null);
  const editing = !!vendor;

  useEffect(() => { setD(fromVendor(vendor)); setError(null); }, [vendor?.id, open]);

  const set = (k: keyof Draft, v: string) => setD((p) => ({ ...p, [k]: v }));

  const onSave = async () => {
    setError(null);
    if (!d.legalName.trim()) return setError(t('vendors.form.legalNameRequired'));
    const body: VendorInput = {
      legalName: d.legalName.trim(),
      displayName: d.displayName || undefined,
      countryCode: d.countryCode ? d.countryCode.toUpperCase().slice(0, 2) : undefined,
      defaultCurrency: d.defaultCurrency ? d.defaultCurrency.toUpperCase().slice(0, 3) : undefined,
      email: d.email || undefined, phone: d.phone || undefined, address: d.address || undefined,
      taxId: d.taxId || undefined, jpCorpNumber: d.jpCorpNumber || undefined,
      jpInvoiceRegistration: d.jpInvoiceRegistration || undefined, preferredRail: d.preferredRail || undefined,
    };
    try {
      const r = editing ? await update.mutateAsync({ id: vendor!.id, patch: body }) : await create.mutateAsync(body);
      onSaved?.(r.id);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Save failed');
    }
  };

  const F = ({ label, k, ph }: { label: string; k: keyof Draft; ph?: string }) => (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <input value={d[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} className={`${inputCn} mt-0.5`} />
    </div>
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t('vendors.editVendor') : t('vendors.newVendor')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium">
            {t('common.cancel')}
          </button>
          <button type="button" onClick={onSave} disabled={create.isPending || update.isPending} className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">{error}</div>}
        <F label={t('vendors.form.legalName')} k="legalName" />
        <F label={t('vendors.form.displayName')} k="displayName" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-ink-500">{t('vendors.drawer.country')}</label>
            <CountrySelect value={d.countryCode} onChange={(v) => set('countryCode', v)} className="mt-0.5" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-ink-500">{t('vendors.drawer.defaultCurrency')}</label>
            <CurrencySelect value={d.defaultCurrency} onChange={(v) => set('defaultCurrency', v)} className="mt-0.5" />
          </div>
          <F label={t('vendors.drawer.jpCorp')} k="jpCorpNumber" />
          <F label={t('vendors.drawer.jpInvoice')} k="jpInvoiceRegistration" ph="T…" />
          <F label={t('vendors.drawer.taxId')} k="taxId" />
          <div>
            <label className="text-[11px] uppercase tracking-wide text-ink-500">{t('vendors.drawer.preferredRail')}</label>
            <RailSelect value={d.preferredRail} onChange={(v) => set('preferredRail', v)} />
          </div>
          <F label={t('common.email')} k="email" />
          <F label={t('vendors.drawer.phone')} k="phone" />
        </div>
        <F label={t('vendors.drawer.address')} k="address" />
      </div>
    </Drawer>
  );
}
